import { useUser } from '@clerk/react';
import { Box, type SxProps, styled, type Theme, Toolbar } from '@mui/material';
import {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { ContentAreaSpinner } from '../components/content-area-spinner';
import AppNavBar from '../components/navigation/AppNavBar';
import BottomNav, { BOTTOM_NAV_HEIGHT } from '../components/navigation/BottomNav';
import SideDrawer from '../components/navigation/SideDrawer';
import { TrialEndingBanner } from '../components/trial-ending-banner';
import {
  hasRequiredPermissions,
  ROUTE_PERMISSION_MAP,
  type RoutePermission,
} from '../config/route-permission-map';
import { useAnalyticsConsentSync } from '../hooks/analytics/use-analytics-consent-sync';
import { useIsDesktop } from '../hooks/use-is-desktop';
import { usePermissions } from '../hooks/use-permissions';

const DRAWER_WIDTH = 260;

// ponytail: MainLayout è montato UNA sola volta come layout-route in
// protected-routes.tsx (la shell stabile → SideDrawer non si rimonta → lo
// scroll del drawer si preserva tra navigazioni). I route file continuano a
// dichiarare `element: <MainLayout />`: quando è annidato dentro la shell
// reale, delega al gate di permission (MainLayoutGate) invece di renderizzare
// di nuovo la shell. L'ordine dei check è identico a pre-refactor, quindi i
// PermissionGuard con redirectTo custom (es. /settings/billing → /settings)
// mantengono la priorità sul redirect generico a `/`.
const InsideMainLayoutContext = createContext(false);

export const ADMIN_ALLOWED_ROUTES = ['/admin', '/team', '/profile', '/settings', '/logout'];

export function isAdminAllowedPath(pathname: string): boolean {
  return ADMIN_ALLOWED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

const WIZARD_ROUTES = [
  '/temperature-check/record',
  '/oil-change/record',
  '/self-check/cleaning/record',
  '/pest-control/record',
  '/goods-receiving/record',
  '/goods-receiving/register',
  '/document-signing',
];

// Data-dense routes (wide tables) get a larger desktop max-width than the
// default 64rem reading column.
const WIDE_ROUTES = [
  '/admin/e-invoices',
  '/admin/label-rule-packs',
  '/admin/ingredients',
  '/admin/partners',
  '/admin/regulatory-news',
  '/admin/tenants',
  '/admin/users',
  '/consultant/client-stats',
  '/haccp/andamento',
  '/lots',
  '/transport-documents',
];

const OutletContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'wide' && prop !== 'fullBleed',
})<{ wide?: boolean; fullBleed?: boolean }>(({ theme, wide, fullBleed }) => ({
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  margin: '0 auto',
  [theme.breakpoints.up('md')]: {
    // Immersive routes (survey runner) draw their own full-bleed background and
    // must span the whole main container, not the 64rem reading column.
    maxWidth: fullBleed ? '100%' : wide ? '90rem' : '64rem',
  },
}));

interface OutletContentProps {
  isUserLoaded: boolean;
  requiredPermission: RoutePermission | undefined;
  permissionsLoading: boolean;
  isAdminRouteAllowed: boolean;
  isRouteAllowed: boolean;
}

function OutletContent({
  isUserLoaded,
  requiredPermission,
  permissionsLoading,
  isAdminRouteAllowed,
  isRouteAllowed,
}: OutletContentProps) {
  if (!isUserLoaded || (requiredPermission && permissionsLoading)) {
    return <ContentAreaSpinner />;
  }
  if (!isAdminRouteAllowed) {
    return <Navigate to="/admin" replace />;
  }
  if (!isRouteAllowed) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

export default function MainLayout() {
  // ponytail: MainLayout è montato una sola volta come layout-route in
  // protected-routes.tsx (shell stabile → SideDrawer non si rimonta → scroll
  // del drawer preservato tra navigazioni). Ogni route file continua a
  // dichiarare il proprio `element: <MainLayout />`: quando è annidato dentro
  // la shell reale, delega al gate di permission (MainLayoutGate) invece di
  // renderizzare di nuovo la shell. Questo mantiene l'ordine originale dei
  // check (PermissionGuard prima del gate generico) e i redirect custom
  // (es. /settings/billing → /settings per Consulente senza BILLING).
  const isNested = useContext(InsideMainLayoutContext);
  if (isNested) {
    return <MainLayoutGate />;
  }
  return (
    <InsideMainLayoutContext.Provider value={true}>
      <MainLayoutShell />
    </InsideMainLayoutContext.Provider>
  );
}

/**
 * Per-route permission gate. Renders inside the stable shell's Outlet.
 * Mirrors exactly the pre-refactor OutletContent wiring so PermissionGuard
 * wrappers (and their custom redirectTo) still take priority over the generic
 * `/` fallback for routes that have both.
 */
function MainLayoutGate() {
  const { pathname } = useLocation();
  const { hasPermission, isLoading: permissionsLoading, roleName } = usePermissions();
  const { isLoaded: isUserLoaded } = useUser();

  // Route-level permission check: find if current path requires a permission
  const requiredPermission = useMemo(() => {
    // Check exact match first, then prefix match for nested routes (e.g. /self-check/temperature/record)
    const exact = ROUTE_PERMISSION_MAP[pathname];
    if (exact) {
      return exact;
    }
    const matchingPath = Object.keys(ROUTE_PERMISSION_MAP).find(
      (routePath) => routePath !== '/' && pathname.startsWith(routePath),
    );
    return matchingPath ? ROUTE_PERMISSION_MAP[matchingPath] : undefined;
  }, [pathname]);

  const isRouteAllowed =
    !requiredPermission || hasRequiredPermissions(requiredPermission, hasPermission);
  const isAdmin = roleName === 'Admin';
  const isAdminRouteAllowed = !isAdmin || isAdminAllowedPath(pathname);

  return (
    <OutletContent
      isUserLoaded={isUserLoaded}
      requiredPermission={requiredPermission}
      permissionsLoading={permissionsLoading}
      isAdminRouteAllowed={isAdminRouteAllowed}
      isRouteAllowed={isRouteAllowed}
    />
  );
}

/**
 * Stable app shell (navbar + drawer + main container + bottom nav).
 * Mounted once via the layout-route in protected-routes.tsx so the SideDrawer
 * scroll position survives cross-route navigation.
 */
function MainLayoutShell() {
  const isDesktop = useIsDesktop();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const { pathname } = useLocation();

  useAnalyticsConsentSync();

  // Close the mobile (temporary) drawer on route change. Before the
  // layout-route refactor this happened implicitly because MainLayout remounted
  // on every navigation (resetting mobileDrawerOpen to false). Now that the
  // shell is stable we restore the close-on-navigate behaviour explicitly so
  // the SwipeableDrawer doesn't linger open over a redirected/new page.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the change trigger, not a body read
  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [pathname]);

  const handleDrawerOpen = useCallback(function handleDrawerOpen() {
    setMobileDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(function handleDrawerClose() {
    setMobileDrawerOpen(false);
  }, []);

  const shouldShowBottomNav = useMemo(() => {
    return !WIZARD_ROUTES.some((route) => pathname.startsWith(route));
  }, [pathname]);

  const isWideRoute = useMemo(
    () => WIDE_ROUTES.some((route) => pathname.startsWith(route)),
    [pathname],
  );

  // Survey runner (/surveys/:templateId) is an immersive full-bleed page.
  // Exclude the list (/surveys) and response detail (/surveys/responses/...).
  const isFullBleedRoute = useMemo(
    () => pathname.startsWith('/surveys/') && !pathname.startsWith('/surveys/responses'),
    [pathname],
  );

  const mainContainerStyle = useMemo(() => {
    const width = isDesktop ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%';
    const paddingBottom = isDesktop
      ? 0
      : shouldShowBottomNav
        ? `calc(${BOTTOM_NAV_HEIGHT}px + var(--safe-area-bottom))`
        : 'var(--safe-area-bottom)';

    const styles: SxProps<Theme> = {
      flexGrow: 1,
      width,
      // Senza minWidth 0 il main (flex item) non scende sotto il proprio
      // min-content: un figlio che si misura in px (il wrapper di recharts,
      // misurato prima che il drawer permanente entrasse in flusso) lo teneva
      // largo quanto il viewport e la pagina scrollava in orizzontale.
      minWidth: 0,
      paddingBottom,
    };

    return styles;
  }, [isDesktop, shouldShowBottomNav]);

  return (
    <Box
      className="main-layout"
      sx={{
        display: 'flex',
        minHeight: '100dvh',
        width: '100%',
        maxWidth: '100%',
        // Guardia: nessuna pagina puo' generare scroll orizzontale di documento
        // (che spezzerebbe anche il gradiente di sfondo del body).
        overflowX: 'clip',
      }}
    >
      <AppNavBar
        className="app-nav-bar"
        onMenuClick={handleDrawerOpen}
        drawerWidth={DRAWER_WIDTH}
      />

      <SideDrawer
        className="side-drawer"
        open={isDesktop || mobileDrawerOpen}
        onClose={handleDrawerClose}
        onOpen={handleDrawerOpen}
        variant={isDesktop ? 'permanent' : 'temporary'}
        drawerWidth={DRAWER_WIDTH}
      />

      <Box component="main" className="main-container" sx={mainContainerStyle}>
        <Toolbar sx={{ marginTop: 'env(safe-area-inset-top, 0px)' }} />
        <TrialEndingBanner />
        <OutletContainer
          className="outlet-container"
          wide={isWideRoute}
          fullBleed={isFullBleedRoute}
        >
          {/* Boundary Suspense UNICA, già montata tra le navigazioni: nessun
              fallback ex-novo per route (era il flash pagina bianca che copriva
              navbar e drawer). Le pagine lazy le carica route.lazy del router;
              questa boundary pesca solo le sospensioni interne delle pagine e
              mostra il loader localizzato nell'area contenuto. */}
          <Suspense fallback={<ContentAreaSpinner />}>
            <Outlet />
          </Suspense>
        </OutletContainer>
      </Box>

      {!isDesktop && shouldShowBottomNav && <BottomNav className="bottom-nav" />}
    </Box>
  );
}
